"use client"
import React, { useEffect, useState, startTransition } from "react"
import * as styles from "./localAttractions.module.scss"
import {
  AttractionType,
  ContentType,
  LocalAttractionsPropsType,
  MapType,
} from "./types"
import {
  Carousel,
  Image,
  Title,
  animateJSX,
} from "@tfehotels/tfe-gatsby-library"
import useEmblaCarousel from "embla-carousel-react"
import { useTransition } from "react-transition-hook"
import RestaurantIcon from "../../../../../static/icons/ADI/Top+Restaurants.svg"
import BarIcon from "../../../../../static/icons/ADI/Best+Bars.svg"
import CafeIcon from "../../../../../static/icons/ADI/Coffee+Shop.svg"
import GymIcon from "../../../../../static/icons/ADI/gym.svg"
import Map from "../../../map"
import { GooglePlaceType } from "../../../map/types"
import { Trans } from "react-i18next"

export default function LocalAttractions({
  data,
  ...props
}: LocalAttractionsPropsType) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    axis: "y",
  })
  const [map, setMap] = useState<MapType>(data.map)
  const [categories, setCategories] = useState<GooglePlaceType[]>([])
  const [content, _setContent] = useState<ContentType>(data.content)
  const mapState = useTransition(!!map.show, 300)
  // const slider1Ref = useRef<Slider>(null)

  // Update Carousel when map has been updated
  useEffect(() => {
    updateCarouselSlide()
  }, [map])

  const handleSetCategories = (newCategories: GooglePlaceType[]) => {
    const set = new Set(categories)
    for (const category of newCategories) {
      if (set.has(category)) {
        set.delete(category) // Remove if present
      } else {
        set.add(category) // Add if not present
      }
    }
    startTransition(() => setCategories(Array.from(set)))
  }

  const updateCarouselSlide = () => {
    if (!emblaApi) return
    const selectedSlide = emblaApi.selectedScrollSnap()
    if (map.selectedMarker !== selectedSlide) {
      emblaApi.scrollTo(map.selectedMarker as number)
    }
  }

  const handleSelectMarker = (markerIndex: number) => {
    if (markerIndex !== map.selectedMarker) {
      startTransition(() => {
        setMap({
          ...map,
          selectedMarker: markerIndex,
        })
      })
      // Otherwise update Carousel
    } else {
      updateCarouselSlide()
    }
  }
  return (
    <section id="map" className={`${styles.section} ${props.className ?? ""}`}>
      {!!content.title && <Title className="title2" {...content.title} />}
      {!map.show && (
        <div key={`map-placeholder${props.pk}`} className="map-placeholder">
          {!!content.placeholder && <Image {...content.placeholder} />}
          <button
            className="btn1"
            onClick={() =>
              startTransition(() => setMap({ ...map, show: true }))
            }
          >
            {!!content.placeholder?.alt ? (
              content.placeholder.alt
            ) : (
              <Trans i18nKey={"Show map"} />
            )}
          </button>
        </div>
      )}
      {mapState.shouldMount && (
        <div
          key={`map-container${props.pk}`}
          className={`container ${animateJSX(mapState)}`}
        >
          <div className="map-wrapper">
            <div className="categories">
              <button
                onClick={() => handleSetCategories(["restaurant"])}
                className={categories.includes("restaurant") ? "active" : ""}
              >
                <img width={24} height={24} src={RestaurantIcon} /> Restaurants
              </button>
              <button
                onClick={() => handleSetCategories(["bar"])}
                className={categories.includes("bar") ? "active" : ""}
              >
                <img width={24} height={24} src={BarIcon} /> Bars
              </button>
              <button
                onClick={() => handleSetCategories(["cafe"])}
                className={categories.includes("cafe") ? "active" : ""}
              >
                <img width={24} height={24} src={CafeIcon} /> Cafes
              </button>
              <button
                onClick={() => handleSetCategories(["gym"])}
                className={categories.includes("gym") ? "active" : ""}
              >
                <img width={24} height={24} src={GymIcon} /> Gyms
              </button>
            </div>
            <div className="map-holder">
              <Map
                center={map.center}
                zoom={map.zoom}
                markers={map.markers}
                selectedMarker={map.selectedMarker}
                handleSelectMarker={handleSelectMarker}
                categories={categories}
              />
            </div>
          </div>
          <div className="attractions">
            <h4>{content.hotel_name}</h4>
            <div className="address">
              {content.hotel_address}
              <div>{content.hotel_phone}</div>
              <div>{content.hotel_email}</div>
            </div>
            <div>
              <Carousel
                id={`map-carousel${props.pk}`}
                emblaRef={emblaRef}
                emblaApi={emblaApi}
                slidesPerView={3}
                slidesPerViewport={{
                  mobile: { slides: 1, height: 300 },
                }}
                gap={5}
                height={264}
                showButtons={true}
                showScroll={false}
                slides={content.attractions.map(
                  (attraction: AttractionType, k: number) => (
                    <div key={`attr${props.pk}-${k}`}>
                      <div
                        className="attr-wrapper"
                        onClick={() => handleSelectMarker(k)}
                      >
                        <div
                          className={`attr-img centered-img${map?.selectedMarker === k ? " active" : ""}`}
                        >
                          {!!attraction.image && (
                            <Image {...(attraction as any)} />
                          )}
                        </div>
                        <div className="attr-info">
                          <strong>{attraction.name}</strong>
                          <span>
                            {!!attraction.distance
                              ? `${(attraction.distance / 1000).toFixed(2)}km`
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
